import { Checkbox, Combobox, Currency, Form } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { REGIE } from '@/constants';
import type { ErrorFieldType } from '@/types';

type RecouvrementTresorerieProps = {
  protocoleImpaye: string;
  comptabilite?: string;
  identifiant: string | number;
  errors?: ErrorFieldType[];
  seuilRecouvrement?: string | number;
  onChange: (value: { [key: string]: unknown }) => void;
};

const RecouvrementTresorerie = (props: RecouvrementTresorerieProps) => {
  const { protocoleImpaye, comptabilite, identifiant, errors, seuilRecouvrement, onChange } = props;

  const comptabiliteOptions = React.useMemo(
    () =>
      _.map(REGIE.COMPTABILITES, (text, index) => ({
        key: `comptabilite@${index}`,
        value: text,
        text,
      })),
    [],
  );

  const isComptabiliteDisabled = protocoleImpaye !== REGIE.PROTOCOLE_IMPAYE_PES_ASAP;

  const handleChange = React.useCallback(
    (e, { name, value }) => {
      if (name === 'seuilRecouvrement') {
        const numberValue = value === '' ? null : _.toNumber(_.replace(value, ',', '.'));

        onChange({
          identifiant,
          [name]:
            value !== '0' && _.isEmpty(value)
              ? null
              : numberValue > 99.0
                ? 99.0
                : // Si NaN on remet la valeur d'avant la saisie
                  _.isNaN(numberValue)
                  ? seuilRecouvrement
                  : _.replace(value, ',', '.'),
        });
      } else {
        onChange({ identifiant, [name]: value });
      }
    },
    [identifiant, onChange, seuilRecouvrement],
  );

  const handleCurrencyChange = React.useCallback(
    (event, value) => {
      const {
        target: { name },
      } = event;

      if (onChange) {
        onChange({
          identifiant,
          [name]: value,
        });
      }
    },
    [identifiant, onChange],
  );

  return (
    <Form>
      <Form.Group inline>
        <Form.Group as={Form.Field} width={9} inline>
          <Form.Field>Protocole de transfert des impayés :</Form.Field>
          <Form.Field>
            <Checkbox
              data-testid="checkbox-protocol-pes-asap"
              radio
              label={REGIE.PROTOCOLE_IMPAYE_PES_ASAP}
              name="protocoleImpaye"
              value={REGIE.PROTOCOLE_IMPAYE_PES_ASAP}
              checked={protocoleImpaye === REGIE.PROTOCOLE_IMPAYE_PES_ASAP}
              onChange={handleChange}
            />
          </Form.Field>
          <Form.Field>
            {/* @ts-expect-error */}
            <Combobox
              menuPortalTarget={document.body}
              data-testid="dropdown-comptabilite"
              selection
              disabled={isComptabiliteDisabled}
              name="comptabilite"
              value={comptabilite || REGIE.COMPTABILITE_JVS}
              options={comptabiliteOptions}
              onChange={handleChange}
              style={{ width: 220 }}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              data-testid="checkbox-protocol-ormc"
              radio
              label={REGIE.PROTOCOLE_IMPAYE_ORMC}
              name="protocoleImpaye"
              value={REGIE.PROTOCOLE_IMPAYE_ORMC}
              checked={protocoleImpaye === REGIE.PROTOCOLE_IMPAYE_ORMC}
              onChange={handleChange}
            />
          </Form.Field>
        </Form.Group>
        <Form.Field
          // @ts-expect-error
          id="seuilRecouvrement"
          width={4}
          error={_.some(errors, { id: 'seuilRecouvrement' })}
        >
          <label>Seuil de recouvrement</label>
          <Currency
            data-testid="poste-comptable-input-seuil-recouvrement"
            value={seuilRecouvrement}
            name="seuilRecouvrement"
            placeholder=""
            // Description vide obligatoire pour que l'input passe en erreur...
            description=""
            style={{ width: 100 }}
            valueMin={0}
            onBlur={handleCurrencyChange}
            devise
          />
        </Form.Field>
      </Form.Group>
    </Form>
  );
};

export default RecouvrementTresorerie;
